// pages/UpgradeTiers.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft,
  FiCheck,
  FiX,
  FiInfo,
  FiLoader,
  FiAlertCircle,
  FiFileText,
  FiCamera,
  FiClock,
  FiHardDrive
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import UpgradeCheckoutForm from '../components/UpgradeCheckoutForm';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import { payForUpgrade, SELLER_STATE } from '../services/upgradeCheckout';
import { detectCountry } from '../services/visitorCountry';
import { quoteForBuyer, formatMoney, scalePrices, albumSizeGb } from '../shared/upgradePricing';
import {
  getEventLinkedAlbums,
  getAlbumExtensionPricing,
  formatStorageSize,
  formatDate as formatAlbumDate,
  getExpiryStatus
} from '../services/albumExtensionService';
import {
  getEventPhotoUploadStatus,
  getPhotoUploadExtensionPricing,
  getExpiryStatusBadge,
  formatDate as formatPhotoDate,
} from '../services/photoUploadExtensionService';
import '../styles/UpgradeTiers.scss';

/**
 * The headline price for a visitor from `country`: rupees before GST in
 * India, the local price with tax included everywhere else. Null until the
 * country is known, or when there is no price for it.
 */
const listPrice = (country, inrBase, localPrices) => {
  if (!country || !(Number(inrBase) > 0)) return null;
  try {
    const q = quoteForBuyer({ country, inrBase, localPrices, customerState: '', sellerState: SELLER_STATE });
    return q.taxInclusive
      ? { amount: formatMoney(q.total, q.currency), note: 'incl. taxes', text: formatMoney(q.total, q.currency) }
      : { amount: formatMoney(q.base, q.currency), note: '+ 18% GST', text: `${formatMoney(q.base, q.currency)} + GST` };
  } catch {
    return null;
  }
};

const TierCard = ({ tier, isCurrent, tierType, onUpgrade, price }) => {
  const isFree = tier.price === 0;
  
  // Host tier features
  const hostFeatures = tierType === 'host' ? [
    {
      text: tier.notification_blasts === -1 
        ? 'Unlimited Notification Blasts' 
        : `${tier.notification_blasts} Notification Blast${tier.notification_blasts > 1 ? 's' : ''}`,
      included: true,
    },
    {
      text: tier.max_attendees === -1 
        ? 'Unlimited Guests' 
        : `Max ${tier.max_attendees} Guests`,
      included: true,
    },
    {
      text: 'Custom RSVP Emoji',
      included: tier.custom_rsvp_emoji,
    },
    {
      text: 'Download Guest List',
      included: tier.guestlist_download,
    },
    {
      text: 'Download RSVP List',
      included: tier.attendee_list_download,
    },
    {
      text: 'Passes Sales Report',
      included: tier.sales_report,
    },
  ] : [];

  // Photo tier features
  const photoFeatures = tierType === 'photo' ? [
    {
      text: `${tier.photo_upload_limit} Photos per Event`,
      included: true,
    },
    {
      text: `Photo Access: ${tier.photo_access_duration}`,
      included: true,
    },
    {
      text: `Upload Photos Within: ${tier.upload_duration}`,
      included: true,
    },
    {
      text: tier.id === 'photo_free'
        ? 'Photos stored forever'
        : 'Valid for 1 year (can be extended)',
      included: true,
    },
  ] : [];

  const features = tierType === 'host' ? hostFeatures : photoFeatures;

  return (
    <motion.div
      className={`tier-card ${isCurrent ? 'current' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
    >
      {isCurrent && (
        <div className="current-badge">
          <span>Current Plan</span>
        </div>
      )}

      <div className="tier-header">
        <h3 className="tier-name">{tier.display_name}</h3>
        {tier.price > 0 ? (
          <div className="price-container">
            <span className="price">{price ? price.amount : '…'}</span>
            {price && <span className="tax-note">{price.note}</span>}
            {tierType === 'photo' && <span className="validity-note">/ year</span>}
          </div>
        ) : (
          <div className="price-container">
            <span className="free-text">Free</span>
            {tierType === 'photo' && <span className="validity-note">Lifetime</span>}
          </div>
        )}
      </div>

      <div className="features-list">
        {features.map((feature, idx) => (
          <div key={idx} className={`feature ${feature.included ? 'included' : 'excluded'}`}>
            {feature.included ? (
              <FiCheck className="icon" />
            ) : (
              <FiX className="icon" />
            )}
            <span>{feature.text}</span>
          </div>
        ))}
      </div>

      <div className="tier-footer">
        {isCurrent ? (
          <div className="current-status">
            <FiCheck size={20} />
            <span>Active Plan</span>
          </div>
        ) : isFree ? (
          <div className="free-status">
            <span>Free Tier</span>
          </div>
        ) : (
          <button 
            className="upgrade-btn"
            onClick={() => onUpgrade(tier)}
          >
            Upgrade to {tier.display_name}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Payment Modal Component
const PaymentModal = ({
  isOpen,
  onClose,
  tier,
  tierType,
  eventId,
  eventName,
  user,
  onPaymentSuccess
}) => {
  const [step, setStep] = useState('details'); // details, processing, success, error
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  // Rupees + GST in India, the tier's local price elsewhere
  // (shared/upgradePricing.js). The server prices it again when charging.
  const quoteFor = useCallback((country, state) => (tier ? quoteForBuyer({
    country,
    inrBase: tier.price,
    localPrices: tier.intl_prices,
    customerState: state,
    sellerState: SELLER_STATE,
  }) : null), [tier]);

  const handlePayment = async (customer) => {
    setBusy(true);
    setError('');
    try {
      const result = await payForUpgrade({
        kind: 'event_tier',
        fields: { eventId, tierId: tier.id },
        customer,
        onStage: (stage) => { if (stage === 'verifying') setStep('processing'); },
      });
      setPaymentResult({
        invoiceNumber: result.invoiceNumber,
        invoiceId: result.invoiceId,
        tierName: tier.display_name,
        amount: formatMoney(result.amount, result.currency),
      });
      setStep('success');
      onPaymentSuccess && onPaymentSuccess(result);
    } catch (err) {
      if (err.cancelled) {
        setStep('details');
      } else {
        setError(err.message || 'Payment failed');
        setStep('error');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="payment-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="payment-modal"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {step === 'details' && (
            <>
              <div className="modal-header">
                <h2>Upgrade to {tier?.display_name}</h2>
                <p className="event-name">{eventName}</p>
              </div>

              <UpgradeCheckoutForm
                user={user}
                quoteFor={quoteFor}
                busy={busy}
                error={error}
                onPay={handlePayment}
                onCancel={onClose}
              />
            </>
          )}

          {step === 'processing' && (
            <div className="modal-processing">
              <FiLoader className="spinner" size={48} />
              <h3>Confirming your payment</h3>
              <p>This takes a few seconds...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="modal-success">
              <div className="success-icon">
                <FiCheck size={48} />
              </div>
              <h3>Payment Successful!</h3>
              <p>Your event has been upgraded to {paymentResult?.tierName}</p>

              {paymentResult?.invoiceNumber && (
                <div className="invoice-info">
                  <FiFileText />
                  <span>Invoice: {paymentResult.invoiceNumber}</span>
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    onClose();
                    window.location.reload();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="modal-error">
              <div className="error-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>Payment Failed</h3>
              <p>{error || 'An error occurred during payment'}</p>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={onClose}>
                  Close
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setStep('details');
                    setError('');
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Album Card Component for Photographer Albums tab
const AlbumCard = ({ album, onExtend }) => {
  const expiryStatus = getExpiryStatus(album.storage_expires_at);

  const getStatusColor = () => {
    switch (expiryStatus.status) {
      case 'expired': return 'status-expired';
      case 'critical': return 'status-critical';
      case 'warning': return 'status-warning';
      case 'soon': return 'status-soon';
      case 'active': return 'status-active';
      default: return 'status-default';
    }
  };

  return (
    <motion.div
      className="album-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
    >
      {/* Album Cover */}
      <div className="album-cover">
        {album.cover_image_url ? (
          <img src={album.cover_image_url} alt={album.name} />
        ) : (
          <div className="placeholder-cover">
            <FiCamera size={32} />
          </div>
        )}
        <div className={`expiry-badge ${getStatusColor()}`}>
          {expiryStatus.label}
        </div>
      </div>

      {/* Album Info */}
      <div className="album-info">
        <h3 className="album-name">{album.name}</h3>

        <div className="album-stats">
          <div className="stat">
            <FiCamera className="icon" />
            <span>{album.photos_count || 0} photos</span>
          </div>
          <div className="stat">
            <FiHardDrive className="icon" />
            <span>{formatStorageSize(album.upload_size_bytes)}</span>
          </div>
          <div className="stat">
            <FiClock className="icon" />
            <span>{formatAlbumDate(album.storage_expires_at)}</span>
          </div>
        </div>

        {album.creator && (
          <div className="photographer-info">
            <span>By: {album.creator.raw_user_meta_data?.first_name || album.creator.email}</span>
          </div>
        )}
      </div>

      {/* Extend Button */}
      <div className="album-footer">
        <button
          className="extend-btn"
          onClick={() => onExtend(album)}
        >
          Extend Storage
        </button>
      </div>
    </motion.div>
  );
};

// Album Extension Modal Component
const AlbumExtensionModal = ({
  isOpen,
  onClose,
  album,
  user,
  pricingTiers,
  onPaymentSuccess
}) => {
  const [step, setStep] = useState('details');
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);

  // Set default duration when modal opens
  useEffect(() => {
    if (isOpen && pricingTiers.length > 0 && !selectedDuration) {
      setSelectedDuration(pricingTiers[0]);
    }
  }, [isOpen, pricingTiers, selectedDuration]);

  const sizeGb = album ? albumSizeGb(album.upload_size_bytes) : 0;

  // Per GB per month, times the album's size and the duration's multiplier -
  // the same sum the server charges (upgrade-create-order).
  const quoteFor = useCallback((country, state) => {
    if (!album || !selectedDuration) return null;
    const units = sizeGb * selectedDuration.duration_multiplier;
    return quoteForBuyer({
      country,
      inrBase: units * Number(selectedDuration.price_per_gb),
      localPrices: scalePrices(selectedDuration.intl_price_per_gb, units),
      customerState: state,
      sellerState: SELLER_STATE,
    });
  }, [album, selectedDuration, sizeGb]);

  const handlePayment = async (customer) => {
    setBusy(true);
    setError('');
    try {
      const result = await payForUpgrade({
        kind: 'album',
        fields: { albumId: album.id, durationMonths: selectedDuration.duration_months },
        customer,
        onStage: (stage) => { if (stage === 'verifying') setStep('processing'); },
      });
      setPaymentResult({
        invoiceNumber: result.invoiceNumber,
        albumName: album.name,
        newExpiry: result.newExpiry,
      });
      setStep('success');
      onPaymentSuccess && onPaymentSuccess(result);
    } catch (err) {
      if (err.cancelled) {
        setStep('details');
      } else {
        setError(err.message || 'Payment failed');
        setStep('error');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setStep('details');
    setError('');
    setPaymentResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="payment-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="payment-modal album-extension-modal"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {step === 'details' && (
            <>
              <div className="modal-header">
                <h2>Extend Album Storage</h2>
                <p className="event-name">{album?.name}</p>
              </div>

              <UpgradeCheckoutForm
                user={user}
                quoteFor={quoteFor}
                busy={busy}
                error={error}
                onPay={handlePayment}
                onCancel={handleClose}
              >
                {/* Album Info */}
                <div className="album-summary">
                  <div className="summary-row">
                    <span>Album Size</span>
                    <span>{formatStorageSize(album?.upload_size_bytes)} (billed as {sizeGb} GB)</span>
                  </div>
                  <div className="summary-row">
                    <span>Current Expiry</span>
                    <span>{formatAlbumDate(album?.storage_expires_at)}</span>
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="duration-selection">
                  <h3>Select Duration</h3>
                  <div className="duration-options">
                    {pricingTiers.map((tier) => (
                      <button
                        key={tier.id}
                        type="button"
                        className={`duration-btn ${selectedDuration?.id === tier.id ? 'active' : ''}`}
                        onClick={() => setSelectedDuration(tier)}
                      >
                        {tier.display_name}
                      </button>
                    ))}
                  </div>
                </div>
              </UpgradeCheckoutForm>
            </>
          )}

          {step === 'processing' && (
            <div className="modal-processing">
              <FiLoader className="spinner" size={48} />
              <h3>Confirming your payment</h3>
              <p>This takes a few seconds...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="modal-success">
              <div className="success-icon">
                <FiCheck size={48} />
              </div>
              <h3>Album Extended!</h3>
              <p>Your album storage has been extended successfully.</p>

              {paymentResult && (
                <div className="success-details">
                  <div className="detail-row">
                    <span>Album</span>
                    <span>{paymentResult.albumName}</span>
                  </div>
                  {paymentResult.newExpiry && (
                    <div className="detail-row">
                      <span>New Expiry</span>
                      <span>{formatAlbumDate(paymentResult.newExpiry)}</span>
                    </div>
                  )}
                  {paymentResult.invoiceNumber && (
                    <div className="detail-row">
                      <FiFileText />
                      <span>Invoice: {paymentResult.invoiceNumber}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleClose();
                    window.location.reload();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="modal-error">
              <div className="error-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>Payment Failed</h3>
              <p>{error || 'An error occurred during payment'}</p>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={handleClose}>
                  Close
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setStep('details');
                    setError('');
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Photo Upload Extension/Upgrade Modal
const PhotoExtensionModal = ({
  isOpen,
  onClose,
  eventId,
  user,
  extensionType, // 'extension' or 'upgrade'
  selectedTierId,
  photoUploadStatus,
  photoExtensionPricing,
  onPaymentSuccess
}) => {
  const [step, setStep] = useState('details');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  const [financialYear, setFinancialYear] = useState(null);

  // The event's financial year is part of its key.
  useEffect(() => {
    if (!isOpen || !eventId) return undefined;
    let cancelled = false;
    supabase
      .from('events')
      .select('financial_year')
      .eq('id', eventId)
      .single()
      .then(({ data }) => { if (!cancelled && data) setFinancialYear(data.financial_year); });
    return () => { cancelled = true; };
  }, [isOpen, eventId]);

  const tierDetails = photoExtensionPricing?.find(t => t.tier_id === selectedTierId);

  const quoteFor = useCallback((country, state) => (tierDetails ? quoteForBuyer({
    country,
    inrBase: tierDetails.extension_price,
    localPrices: tierDetails.intl_prices,
    customerState: state,
    sellerState: SELLER_STATE,
  }) : null), [tierDetails]);

  const handleClose = () => {
    setStep('details');
    setError('');
    setPaymentResult(null);
    onClose();
  };

  const handlePayment = async (customer) => {
    if (!financialYear) {
      setError('Still loading the event. Please try again in a moment.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await payForUpgrade({
        kind: 'photo_upload',
        fields: { eventId, financialYear, purchaseType: extensionType, tierId: selectedTierId },
        customer,
        onStage: (stage) => { if (stage === 'verifying') setStep('processing'); },
      });
      setPaymentResult({
        invoiceNumber: result.invoiceNumber,
        newExpiryDate: result.newExpiry,
        photoLimit: tierDetails?.photo_limit,
      });
      setStep('success');
      onPaymentSuccess && onPaymentSuccess(result);
    } catch (err) {
      if (err.cancelled) {
        setStep('details');
      } else {
        setError(err.message || 'Payment failed');
        setStep('error');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="payment-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="payment-modal photo-extension-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3>
              {extensionType === 'extension' ? 'Extend Photo Upload' : 'Upgrade Photo Upload Tier'}
            </h3>
            <button className="close-btn" onClick={handleClose} aria-label="Close">
              <FiX />
            </button>
          </div>

          {step === 'details' && (
            <UpgradeCheckoutForm
              user={user}
              quoteFor={quoteFor}
              busy={busy}
              error={error}
              onPay={handlePayment}
              onCancel={handleClose}
            >
              <div className="extension-summary">
                <h4>
                  {extensionType === 'extension'
                    ? `Extend ${photoUploadStatus?.tierDisplayName || 'Current Tier'}`
                    : `Upgrade to ${tierDetails?.display_name || 'Higher Tier'}`}
                </h4>
                <div className="summary-details">
                  <div className="detail-row">
                    <span className="label">Photo Limit:</span>
                    <span className="value">{tierDetails?.photo_limit?.toLocaleString()} photos</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Validity:</span>
                    <span className="value">1 Year from {extensionType === 'extension' ? 'now' : 'upgrade date'}</span>
                  </div>
                  {photoUploadStatus?.expiresAt && extensionType === 'extension' && (
                    <div className="detail-row">
                      <span className="label">Current Expiry:</span>
                      <span className="value">{formatPhotoDate(photoUploadStatus.expiresAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </UpgradeCheckoutForm>
          )}

          {step === 'processing' && (
            <div className="modal-processing">
              <div className="spinner-large"></div>
              <h3>Confirming your payment</h3>
              <p>This takes a few seconds...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="modal-success">
              <div className="success-icon">
                <FiCheck size={48} />
              </div>
              <h3>
                {extensionType === 'extension'
                  ? 'Extension Successful!'
                  : 'Upgrade Successful!'}
              </h3>
              <p>
                Your photo upload tier has been {extensionType === 'extension' ? 'extended' : 'upgraded'} successfully.
              </p>

              {paymentResult && (
                <div className="success-details">
                  {paymentResult.photoLimit != null && (
                    <div className="detail-row">
                      <FiCamera />
                      <span>Photo Limit: {paymentResult.photoLimit.toLocaleString()} photos</span>
                    </div>
                  )}
                  {paymentResult.newExpiryDate && (
                    <div className="detail-row">
                      <FiClock />
                      <span>Valid Until: {formatPhotoDate(paymentResult.newExpiryDate)}</span>
                    </div>
                  )}
                  {paymentResult.invoiceNumber && (
                    <div className="detail-row">
                      <FiFileText />
                      <span>Invoice: {paymentResult.invoiceNumber}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => {
                    handleClose();
                    window.location.reload();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="modal-error">
              <div className="error-icon">
                <FiAlertCircle size={48} />
              </div>
              <h3>Payment Failed</h3>
              <p>{error || 'An error occurred during payment'}</p>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={handleClose}>
                  Close
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setStep('details');
                    setError('');
                  }}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const UpgradeTiers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const eventId = searchParams.get('eventId');
  const eventName = searchParams.get('eventName');

  // The tab for why they came: ?tab=photo or ?tab=albums when a link is about
  // those, Host otherwise - guest limits, blasts and the plain Upgrade button
  // are all host features. It always opened on Photo, whatever the reason.
  const tabParam = searchParams.get('tab');
  const [selectedTab, setSelectedTab] = useState(
    tabParam === 'photo' || tabParam === 'albums' ? tabParam : 'host'
  );
  const [loading, setLoading] = useState(true);
  const [hostTiers, setHostTiers] = useState([]);
  const [photoTiers, setPhotoTiers] = useState([]);
  const [currentHostTier, setCurrentHostTier] = useState(null);
  const [currentPhotoTier, setCurrentPhotoTier] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Album extension state
  const [albums, setAlbums] = useState([]);
  const [albumPricingTiers, setAlbumPricingTiers] = useState([]);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);

  // Photo upload extension state
  const [photoUploadStatus, setPhotoUploadStatus] = useState(null);
  const [photoExtensionPricing, setPhotoExtensionPricing] = useState([]);
  const [, setLoadingPhotoStatus] = useState(false);
  const [showPhotoExtensionModal, setShowPhotoExtensionModal] = useState(false);
  const [photoExtensionType, setPhotoExtensionType] = useState(null); // 'extension' or 'upgrade'
  const [selectedPhotoTier, setSelectedPhotoTier] = useState(null);

  // Prices are shown in the visitor's currency; checkout lets them change it.
  const [country, setCountry] = useState(null);
  useEffect(() => {
    let cancelled = false;
    detectCountry().then((c) => { if (!cancelled) setCountry(c); });
    return () => { cancelled = true; };
  }, []);
  const inIndia = country === 'IN';

  const loadCurrentTiers = useCallback(async () => {
    try {
      const { data: event, error } = await supabase
        .from('events')
        .select('host_pricing_tier_id, photo_pricing_tier_id')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      
      setCurrentHostTier(event.host_pricing_tier_id);
      setCurrentPhotoTier(event.photo_pricing_tier_id);
    } catch (error) {
      console.error('Error loading current tiers:', error);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      loadTiers();
      loadCurrentTiers();
    }
  }, [eventId, loadCurrentTiers]);

  // Load albums when album tab is selected
  const loadAlbums = useCallback(async () => {
    try {
      setLoadingAlbums(true);
      const result = await getEventLinkedAlbums(eventId);
      if (result.success) {
        setAlbums(result.albums);
      } else {
        console.error('Error loading albums:', result.error);
      }
    } catch (error) {
      console.error('Error loading albums:', error);
    } finally {
      setLoadingAlbums(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (selectedTab === 'albums' && eventId) {
      loadAlbums();
      loadAlbumPricing();
    }
  }, [selectedTab, eventId, loadAlbums]);

  // Load photo upload status when photo tab is selected
  const loadPhotoUploadStatus = useCallback(async () => {
    try {
      setLoadingPhotoStatus(true);

      // Get financial year from event
      const { data: event } = await supabase
        .from('events')
        .select('financial_year')
        .eq('id', eventId)
        .single();

      if (event) {
        const result = await getEventPhotoUploadStatus(eventId, event.financial_year);
        if (result.success) {
          setPhotoUploadStatus(result.status);
        } else {
          console.error('Error loading photo upload status:', result.error);
        }
      }
    } catch (error) {
      console.error('Error loading photo upload status:', error);
    } finally {
      setLoadingPhotoStatus(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (selectedTab === 'photo' && eventId) {
      loadPhotoUploadStatus();
      loadPhotoExtensionPricing();
    }
  }, [selectedTab, eventId, loadPhotoUploadStatus]);

  const loadTiers = async () => {
    try {
      setLoading(true);
      
      // Load host tiers
      const { data: hostData, error: hostError } = await supabase
        .from('event_pricing_tiers')
        .select('*')
        .eq('tier_type', 'host')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (hostError) throw hostError;
      setHostTiers(hostData || []);
      
      // Load photo tiers
      const { data: photoData, error: photoError } = await supabase
        .from('event_pricing_tiers')
        .select('*')
        .eq('tier_type', 'photo')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (photoError) throw photoError;
      setPhotoTiers(photoData || []);
      
    } catch (error) {
      console.error('Error loading tiers:', error);
      alert('Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const loadAlbumPricing = async () => {
    try {
      const result = await getAlbumExtensionPricing();
      if (result.success) {
        setAlbumPricingTiers(result.pricing);
      }
    } catch (error) {
      console.error('Error loading album pricing:', error);
    }
  };

  // Load photo upload status and expiry info
  const loadPhotoExtensionPricing = async () => {
    try {
      const result = await getPhotoUploadExtensionPricing();
      if (result.success) {
        setPhotoExtensionPricing(result.pricing);
      }
    } catch (error) {
      console.error('Error loading photo extension pricing:', error);
    }
  };

  const handleUpgrade = (tier) => {
    setSelectedTier(tier);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (result) => {
    console.log('Payment successful:', result);
    // Refresh current tiers
    loadCurrentTiers();
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedTier(null);
  };

  // Album extension handlers
  const handleExtendAlbum = (album) => {
    setSelectedAlbum(album);
    setShowAlbumModal(true);
  };

  const handleCloseAlbumModal = () => {
    setShowAlbumModal(false);
    setSelectedAlbum(null);
  };

  const handleAlbumPaymentSuccess = (result) => {
    console.log('Album extension successful:', result);
    // Refresh albums
    loadAlbums();
  };

  // Photo upload extension/upgrade handlers
  const handleExtendPhotoUpload = () => {
    setPhotoExtensionType('extension');
    setSelectedPhotoTier(photoUploadStatus?.currentTierId || null);
    setShowPhotoExtensionModal(true);
  };

  const handleUpgradePhotoUpload = (tierId) => {
    setPhotoExtensionType('upgrade');
    setSelectedPhotoTier(tierId);
    setShowPhotoExtensionModal(true);
  };

  const handleClosePhotoExtensionModal = () => {
    setShowPhotoExtensionModal(false);
    setPhotoExtensionType(null);
    setSelectedPhotoTier(null);
  };

  const handlePhotoExtensionSuccess = (result) => {
    console.log('Photo upload extension/upgrade successful:', result);
    // Refresh photo upload status
    loadPhotoUploadStatus();
    loadCurrentTiers();
  };

  // Photo upload extension and upgrade are priced from photo_upload_extension_pricing.
  const photoPrice = (tierId, fallbackInr) => {
    const row = photoExtensionPricing.find((t) => t.tier_id === tierId);
    return listPrice(country, row ? row.extension_price : fallbackInr, row?.intl_prices);
  };
  const extensionPrice = photoPrice(photoUploadStatus?.currentTierId, photoUploadStatus?.extensionPrice);
  const nextTierPrice = photoPrice(photoUploadStatus?.nextTierId, photoUploadStatus?.nextTierPrice);

  const currentTiers = selectedTab === 'host' ? hostTiers : photoTiers;
  const currentTierId = selectedTab === 'host' ? currentHostTier : currentPhotoTier;

  if (!eventId) {
    return (
      <DashboardLayout>
        <div className="upgrade-tiers-page">
          <div className="error-state">
            <h3>No event selected</h3>
            <p>Please select an event to upgrade</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/upgrade')}
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="upgrade-tiers-page">
        <div className="tiers-container">
          {/* Header */}
          <motion.div 
            className="tiers-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button 
              className="back-btn"
              onClick={() => navigate('/upgrade')}
            >
              <FiArrowLeft size={22} />
            </button>
            
            <div className="header-content">
              <h1>Upgrade Invitation</h1>
              <p className="event-name">{eventName}</p>
            </div>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div 
            className="tabs-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <button
              className={`tab ${selectedTab === 'host' ? 'active' : ''}`}
              onClick={() => setSelectedTab('host')}
            >
              Host Features
            </button>
            <button
              className={`tab ${selectedTab === 'photo' ? 'active' : ''}`}
              onClick={() => setSelectedTab('photo')}
            >
              Photo Upload
            </button>
            <button
              className={`tab ${selectedTab === 'albums' ? 'active' : ''}`}
              onClick={() => setSelectedTab('albums')}
            >
              Photographer Albums
            </button>
          </motion.div>

          {/* Section Info */}
          <motion.div
            className="section-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2>
              {selectedTab === 'host'
                ? 'Host Plans'
                : selectedTab === 'photo'
                  ? 'Photo Upload Plans'
                  : 'Photographer Albums'}
            </h2>
            <p>
              {selectedTab === 'host'
                ? 'Unlock powerful features to manage your event'
                : selectedTab === 'photo'
                  ? 'Share more memories with higher photo limits'
                  : 'Extend storage for albums linked to this event'}
            </p>
          </motion.div>

          {/* Content Grid */}
          <div className="tiers-content">
            {selectedTab === 'albums' ? (
              // Albums Tab Content
              loadingAlbums ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading albums...</p>
                </div>
              ) : albums.length === 0 ? (
                <div className="empty-state">
                  <FiCamera size={48} />
                  <h3>No Albums Found</h3>
                  <p>No photographer albums are linked to this event yet.</p>
                </div>
              ) : (
                <motion.div
                  className="albums-grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {albums.map((album, index) => (
                    <motion.div
                      key={album.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <AlbumCard
                        album={album}
                        onExtend={handleExtendAlbum}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )
            ) : selectedTab === 'photo' ? (
              // Photo Tab Content
              loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading pricing tiers...</p>
                </div>
              ) : (
                <>
                  {/* Show tier status card if user has paid tier and photos uploaded */}
                  {photoUploadStatus && currentPhotoTier && currentPhotoTier !== 'photo_free' && photoUploadStatus.photosCount > 0 && (
                    <div className="photo-status-container">
                      {/* Current Tier Status Card */}
                      <motion.div
                        className="photo-status-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className="status-header">
                          <div className="tier-info">
                            <h3>{photoUploadStatus.tierDisplayName}</h3>
                            <p>{photoUploadStatus.photoLimit?.toLocaleString('en-IN')} Photos</p>
                          </div>
                          {photoUploadStatus.expiresAt && (
                            <div className={`expiry-badge ${getExpiryStatusBadge(photoUploadStatus.daysUntilExpiry, photoUploadStatus.isExpired).variant}`}>
                              <FiClock />
                              <span>{getExpiryStatusBadge(photoUploadStatus.daysUntilExpiry, photoUploadStatus.isExpired).label}</span>
                            </div>
                          )}
                        </div>

                        <div className="status-details">
                          <div className="detail-row">
                            <span className="label">Photos Uploaded:</span>
                            <span className="value">{photoUploadStatus.photosCount || 0} / {photoUploadStatus.photoLimit?.toLocaleString('en-IN')}</span>
                          </div>
                          {photoUploadStatus.expiresAt && (
                            <div className="detail-row">
                              <span className="label">Expires On:</span>
                              <span className="value">{formatPhotoDate(photoUploadStatus.expiresAt)}</span>
                            </div>
                          )}
                          {photoUploadStatus.activatedAt && (
                            <div className="detail-row">
                              <span className="label">Activated On:</span>
                              <span className="value">{formatPhotoDate(photoUploadStatus.activatedAt)}</span>
                            </div>
                          )}
                        </div>

                        <div className="status-actions">
                          {photoUploadStatus.canExtend && (
                            <button
                              className="btn btn-primary"
                              onClick={handleExtendPhotoUpload}
                            >
                              <FiClock />
                              Extend for 1 Year{extensionPrice ? ` (${extensionPrice.text})` : ''}
                            </button>
                          )}
                          {photoUploadStatus.canUpgrade && photoUploadStatus.nextTierId && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleUpgradePhotoUpload(photoUploadStatus.nextTierId)}
                            >
                              Upgrade to {photoUploadStatus.nextTierDisplayName}{nextTierPrice ? ` (${nextTierPrice.text})` : ''}
                            </button>
                          )}
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Always show tier cards for initial purchase or upgrades */}
                  <motion.div
                    className="tiers-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {currentTiers.map((tier, index) => (
                      <motion.div
                        key={tier.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <TierCard
                          tier={tier}
                          isCurrent={currentTierId === tier.id}
                          tierType={selectedTab}
                          onUpgrade={handleUpgrade}
                          price={listPrice(country, tier.price, tier.intl_prices)}
                        />
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Extension Section - Always visible */}
                  <motion.div
                    className="extension-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="extension-header">
                      <FiClock size={24} />
                      <div>
                        <h3>Photo Upload Extension</h3>
                        <p>Extend your photo upload tier validity by 1 year</p>
                      </div>
                    </div>

                    {currentPhotoTier === 'photo_free' || (photoUploadStatus?.photosCount || 0) <= 100 ? (
                      <div className="extension-disabled">
                        <p className="disabled-message">
                          {currentPhotoTier === 'photo_free'
                            ? 'Upgrade to a paid tier to enable extensions'
                            : 'Upload more than 100 photos to enable extensions'}
                        </p>
                        <button className="btn-extension" disabled>
                          Extension Available After Upgrade
                        </button>
                      </div>
                    ) : (
                      <div className="extension-enabled">
                        <p className="enabled-message">
                          Your {photoUploadStatus?.tierDisplayName} tier can be extended
                        </p>
                        <button
                          className="btn-extension"
                          onClick={handleExtendPhotoUpload}
                        >
                          <FiClock />
                          Extend for 1 Year{extensionPrice ? ` (${extensionPrice.text})` : ''}
                        </button>
                      </div>
                    )}
                  </motion.div>
                </>
              )
            ) : (
              // Host Tiers - Show Normal Tier Cards
              loading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading pricing tiers...</p>
                </div>
              ) : (
                <motion.div
                  className="tiers-grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {currentTiers.map((tier, index) => (
                    <motion.div
                      key={tier.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <TierCard
                        tier={tier}
                        isCurrent={currentTierId === tier.id}
                        tierType={selectedTab}
                        onUpgrade={handleUpgrade}
                        price={listPrice(country, tier.price, tier.intl_prices)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )
            )}
          </div>

          {/* Info Footer */}
          <motion.div
            className="info-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="info-icon">
              <FiInfo />
            </div>
            <div className="info-text">
              <p>
                {country === null
                  ? 'Prices are shown in your local currency.'
                  : inIndia
                    ? 'All prices are in Indian Rupees (₹) and subject to 18% GST.'
                    : 'Prices are in your local currency and include 18% IGST. Choose your country at checkout if it looks wrong.'}
              </p>
              <p>Upgrades are event-specific and will apply only to the selected invitation.</p>
            </div>
          </motion.div>
        </div>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={handleClosePaymentModal}
          tier={selectedTier}
          tierType={selectedTab}
          eventId={eventId}
          eventName={eventName}
          user={user}
          onPaymentSuccess={handlePaymentSuccess}
        />

        {/* Album Extension Modal */}
        <AlbumExtensionModal
          isOpen={showAlbumModal}
          onClose={handleCloseAlbumModal}
          album={selectedAlbum}
          user={user}
          pricingTiers={albumPricingTiers}
          onPaymentSuccess={handleAlbumPaymentSuccess}
        />

        {/* Photo Upload Extension/Upgrade Modal */}
        <PhotoExtensionModal
          isOpen={showPhotoExtensionModal}
          onClose={handleClosePhotoExtensionModal}
          eventId={eventId}
          user={user}
          extensionType={photoExtensionType}
          selectedTierId={selectedPhotoTier}
          photoUploadStatus={photoUploadStatus}
          photoExtensionPricing={photoExtensionPricing}
          onPaymentSuccess={handlePhotoExtensionSuccess}
        />
      </div>
    </DashboardLayout>
  );
};

export default UpgradeTiers;